import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, AlertTriangle } from "lucide-react";
import { findAppeal } from "@/data/appeals";
import { useArticle } from "@/lib/useArticles";

export default function AppealDetail() {
  const { slug } = useParams();
  const staticA = findAppeal(slug);
  const dbA = useArticle(slug);

  if (dbA === undefined) {
    return <div className="max-w-[1400px] mx-auto px-6 py-24 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>;
  }
  const a = dbA || staticA;
  if (!a) return <Navigate to="/offer-help" replace />;

  return (
    <article data-testid="appeal-detail" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-16">
      <Link
        to="/offer-help"
        data-testid="back-to-offer-help"
        className="inline-flex items-center gap-2 text-sm mb-8"
        style={{ color: "var(--wofy-text-soft)" }}
      >
        <ArrowLeft className="w-4 h-4" /> Offer Help
      </Link>

      {/* URGENT HERO */}
      <header
        className="relative rounded-3xl overflow-hidden p-8 md:p-12 lg:p-14 border"
        style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
      >
        <span aria-hidden className="pointer-events-none absolute -top-16 -left-10 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: "#B23A3A" }} />
        <span aria-hidden className="pointer-events-none absolute -bottom-24 right-0 w-80 h-80 rounded-full blur-3xl opacity-20" style={{ background: "var(--wofy-primary)" }} />

        {a.featured_image && !a.hero_image && (
          <div className="relative aspect-[16/6] w-full rounded-2xl overflow-hidden mb-8">
            <img src={a.featured_image} alt={a.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="relative">
          <div className="flex flex-wrap items-center gap-3">
            {a.urgency && (
              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-wider"
                style={{ background: "#B23A3A", color: "#FDFBF7" }}
                data-testid="urgency-tag"
              >
                <AlertTriangle className="w-3 h-3" /> {a.urgency}
              </span>
            )}
            {a.overline && <span className="overline">{a.overline}</span>}
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mt-6 max-w-4xl">
            {a.title}
          </h1>
          {a.subtitle && (
            <p className="mt-6 max-w-3xl text-base md:text-lg leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
              {a.subtitle}
            </p>
          )}
          {a.location && <div className="overline mt-6">{a.location}</div>}
        </div>
      </header>

      {/* STATS */}
      {a.stats?.length > 0 && (
        <div className="mt-10 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-y py-10" style={{ borderColor: "var(--wofy-border)" }}>
          {a.stats.map((s, i) => (
            <div key={`stat-${i}`}>
              <div className="font-display text-3xl md:text-4xl" style={{ color: "#B23A3A" }}>{s.k}</div>
              <div className="overline mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* INTRO */}
      {(a.intro || a.lead) && (
        <div className="mt-16 grid lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-3">
            <div className="overline">Introduction</div>
          </div>
          <div className="lg:col-span-9 space-y-5">
            {a.intro && <p className="text-lg leading-relaxed font-display">{a.intro}</p>}
            {a.lead && <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>{a.lead}</p>}
          </div>
        </div>
      )}

      {/* SECTIONS */}
      {a.sections?.length > 0 && (
        <div className="mt-16 space-y-14 md:space-y-16">
          {a.sections.map((sec, si) => (
            <section key={`${sec.heading}-${si}`} className="grid lg:grid-cols-12 gap-8 md:gap-12">
              <div className="lg:col-span-3">
                <h2 className="font-display text-2xl leading-tight">{sec.heading}</h2>
              </div>
              <div className="lg:col-span-9 space-y-5">
                {sec.body?.map((p, i) => (
                  <p key={`${si}-p-${i}`} className="text-base leading-relaxed">{p}</p>
                ))}
                {sec.list?.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {sec.list.map((li, i) => (
                      <li key={`${si}-l-${i}`} className="flex items-start gap-3 text-base leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#B23A3A" }} />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {sec.outro && (
                  <p className="text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>{sec.outro}</p>
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* DONATION PARTNERS */}
      {a.donation_partners?.length > 0 && (
        <div className="mt-20">
          <div className="overline">TRUSTED ORGANIZATIONS ACCEPTING DONATIONS</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3 max-w-3xl">
            Every dollar reaches survivors faster than shipped supplies.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {a.donation_partners.map((p) => (
              <div key={p.name} data-testid={`partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="wofy-card">
                <div className="font-display text-xl">{p.name}</div>
                {p.description && <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>{p.description}</p>}
                <div className="mt-5 flex flex-wrap gap-4 text-sm">
                  {p.response && (
                    <a href={p.response} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium" style={{ color: "var(--wofy-primary)" }}>
                      Donate now <ArrowUpRight className="w-4 h-4" />
                    </a>
                  )}
                  {p.website && (
                    <a href={p.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
                      Official site <ArrowUpRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HOW TO HELP */}
      {a.how_to_help?.length > 0 && (
        <div className="mt-20 grid lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl leading-tight">How ordinary people can help</h2>
          </div>
          <div className="lg:col-span-9">
            <p className="mb-5">You don&rsquo;t need to be a disaster expert to make a meaningful difference.</p>
            <ul className="space-y-2">
              {a.how_to_help.map((li, i) => (
                <li key={`h-${i}`} className="flex items-start gap-3 text-base leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--wofy-primary)" }} />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5" style={{ color: "var(--wofy-text-soft)" }}>Long-term support is often just as important as immediate emergency relief.</p>
          </div>
        </div>
      )}

      {/* OUTLOOK */}
      {a.outlook?.body?.length > 0 && (
        <div className="mt-20 grid lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl leading-tight">{a.outlook.heading}</h2>
          </div>
          <div className="lg:col-span-9 space-y-5">
            {a.outlook.body.map((p, i) => (
              <p key={`outlook-${i}`} className="text-base leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      )}

      {/* CLOSING */}
      {a.closing?.body?.length > 0 && (
        <div className="mt-20 grid lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl leading-tight">{a.closing.heading}</h2>
          </div>
          <div className="lg:col-span-9 space-y-5">
            {a.closing.body.map((p, i) => (
              <p key={`closing-${i}`} className="text-base leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      )}

      {/* DISCLAIMER */}
      {a.disclaimer && (
        <p className="mt-16 text-xs leading-relaxed border-t pt-6" style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-border)" }}>
          <strong>Note:</strong> {a.disclaimer}
        </p>
      )}
    </article>
  );
}
